import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Eye, EyeSlash } from "@phosphor-icons/react";

const COMPANY_LOGO = process.env.REACT_APP_COMPANY_LOGO;
const COMPANY_NAME = process.env.REACT_APP_COMPANY_NAME || "Vision AcryGlas";

export default function AdminSetupPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/setup`, { name, email, password });
      localStorage.setItem("adminToken", response.data.token);
      localStorage.setItem("admin", JSON.stringify(response.data.admin));
      toast.success("Admin account created successfully!");
      navigate("/admin");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Admin already exists. Please login instead.");
        navigate("/admin/login");
      } else {
        toast.error(error.response?.data?.detail || "Setup failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0A0A]" data-testid="admin-setup-page">
      {/* Logo Header */}
      <div className="py-8 px-4 flex flex-col items-center">
        <img 
          src={COMPANY_LOGO} 
          alt={COMPANY_NAME} 
          className="h-16 object-contain mb-2"
        />
        <div className="flex items-center gap-2 mt-2">
          <ShieldCheck size={20} className="text-[#10B981]" weight="fill" />
          <p className="text-sm text-[#9CA3AF] font-medium">Admin Setup</p>
        </div>
      </div>
      
      {/* Setup Form */}
      <div className="flex-1 bg-white rounded-t-3xl px-6 pt-8 pb-12">
        <div className="max-w-md mx-auto">
          <h1 className="text-3xl font-bold text-[#0A0A0A] mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Create Admin Account
          </h1>
          <p className="text-[#4B5563] mb-8">Set up the first admin for {COMPANY_NAME}</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-[#0A0A0A] font-medium">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Admin Name"
                className="mt-1.5 h-12 border-[#E5E7EB]"
                data-testid="admin-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-[#0A0A0A] font-medium">
                Admin Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@visionacryglas.com"
                className="mt-1.5 h-12 border-[#E5E7EB]"
                data-testid="admin-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#0A0A0A] font-medium">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="h-12 pr-12 border-[#E5E7EB]"
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#4B5563]"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#10B981] hover:bg-[#059669] text-white font-semibold rounded-full mt-6"
              data-testid="create-admin-button"
            >
              {loading ? "Creating..." : "Create Admin Account"}
            </Button>
          </form>

          <p className="text-center mt-8 text-[#9CA3AF] text-sm">
            Already have an admin?{" "}
            <button 
              onClick={() => navigate("/admin/login")}
              className="text-[#0055FF] font-medium hover:underline"
            >
              Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
